import i18next from 'i18next';
import { UpdateMessageMap } from './types';
import logger from '../../lib/logger';
import store from '../../store';
import { setUsage } from '../../features/plan/planSlice';
import { RootState } from '../../reducers';
import {
  fetchFileUpdate,
  updateJobStatus,
  setTrimStatus,
} from '../../features/selectedVideo/selectedVideoSlice';
import { updateNotification } from '../../features/dashboard/dashboardSlice';
import { getFileUrl } from '../../lib/routingUtils';
import {
  updateFile,
  triggerFileListRefresh,
} from '../../features/library/librarySlice';
import { handleRagUpdate } from './ragUpdate';
import { getInitialState } from '../../features/app/appSlice';
import { addTeamMember } from '../../features/team/teamSlice';

// Utility function to extract user-friendly information from URLs
const extractUserFriendlyUrlInfo = (
  url: string,
): { source: string; displayName: string } => {
  // Handle the fallback case for unknown/undefined URLs
  if (url === 'Unknown source') {
    return {
      source: 'Unknown Source',
      displayName: 'Imported content',
    };
  }

  // Handle screenapp:// URLs (from microtool uploads)
  if (url.startsWith('screenapp://')) {
    try {
      // Extract the JWT token and decode the payload to get the filename
      const token = url.slice('screenapp://'.length);
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = parts[1];
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const decoded = atob(base64);
        const data = JSON.parse(decoded);
        // The file path is in 'sub' field (e.g., "temp/abc123/filename.txt")
        const filePath = data.sub || '';
        const fileName = filePath.split('/').pop() || 'document';
        // Remove extension for cleaner display
        const displayName = fileName.replace(/\.[^.]+$/, '') || 'document';
        return {
          source: 'ScreenApp',
          displayName,
        };
      }
    } catch {
      // If decoding fails, return generic message
    }
    return {
      source: 'ScreenApp',
      displayName: 'document',
    };
  }

  try {
    const urlObj = new URL(url);
    const { hostname, pathname, searchParams } = urlObj;
    const lowerHostname = hostname.toLowerCase();

    // Handle known platforms
    if (
      lowerHostname.includes('youtube.com') ||
      lowerHostname.includes('youtu.be')
    ) {
      const videoId = lowerHostname.includes('youtu.be')
        ? pathname.slice(1)
        : searchParams.get('v');
      return {
        source: 'YouTube',
        displayName: videoId ? `YouTube video (${videoId})` : 'YouTube video',
      };
    }

    if (lowerHostname.includes('vimeo.com')) {
      const videoId = pathname.split('/').pop();
      return {
        source: 'Vimeo',
        displayName: videoId ? `Vimeo video (${videoId})` : 'Vimeo video',
      };
    }

    if (
      lowerHostname.includes('facebook.com') ||
      lowerHostname.includes('fb.watch')
    ) {
      return {
        source: 'Facebook',
        displayName: 'Facebook video',
      };
    }

    if (lowerHostname.includes('instagram.com')) {
      return {
        source: 'Instagram',
        displayName: 'Instagram video',
      };
    }

    if (lowerHostname.includes('linkedin.com')) {
      return {
        source: 'LinkedIn',
        displayName: 'LinkedIn video',
      };
    }

    if (
      lowerHostname.includes('twitter.com') ||
      lowerHostname.includes('x.com')
    ) {
      return {
        source: 'Twitter/X',
        displayName: 'Twitter/X video',
      };
    }

    // Handle direct file URLs or cloud storage
    if (
      lowerHostname.includes('storage.googleapis.com') ||
      lowerHostname.includes('s3.amazonaws.com') ||
      lowerHostname.includes('amazonaws.com')
    ) {
      // For cloud storage, use generic user-friendly terms
      return {
        source: 'ScreenApp',
        displayName: 'recording',
      };
    }

    // Handle other file-like URLs
    const fileExtensionMatch = pathname.match(/\.([a-zA-Z0-9]+)(\?|$)/);
    if (fileExtensionMatch) {
      const extension = fileExtensionMatch[1].toLowerCase();
      const videoExtensions = [
        'mp4',
        'mov',
        'avi',
        'wmv',
        'flv',
        'mkv',
        'webm',
      ];
      const audioExtensions = ['mp3', 'wav', 'ogg', 'm4a', 'aac'];

      if (
        videoExtensions.includes(extension) ||
        audioExtensions.includes(extension)
      ) {
        // Use user-friendly terms for media files instead of technical filenames
        const mediaType = videoExtensions.includes(extension)
          ? 'recording'
          : 'audio recording';
        return {
          source: hostname,
          displayName: mediaType,
        };
      }
    }

    // Fallback for other URLs
    return {
      source: hostname,
      displayName: `Content from ${hostname}`,
    };
  } catch {
    // If URL parsing fails, try to extract some basic info
    const parts = url.split('/');
    const lastPart = parts[parts.length - 1];

    if (lastPart && lastPart.includes('.')) {
      return {
        source: 'External Source',
        displayName: lastPart.split('?')[0],
      };
    }

    return {
      source: 'External Source',
      displayName: 'Imported content',
    };
  }
};

// Store import URLs temporarily until backend includes them
const importUrlCache: Map<string, string> = new Map();

// Export function to get cached import URL
export const getCachedImportUrl = (fileId: string): string | undefined => {
  return importUrlCache.get(fileId);
};

const handleImportUpdate = async (
  payload: UpdateMessageMap['import']['payload'],
) => {
  const { dispatch } = store;
  const { teamId, parentId, fileId, status, error, errorCode, url } = payload;

  // Cache the URL for this import
  if (url && fileId) {
    importUrlCache.set(fileId, url);
    console.log('Cached import URL:', { fileId, url });
  }

  // Add notification based on status
  let title;
  let message;
  let link;
  const t = i18next.t.bind(i18next);

  if (status === 'failed') {
    // Extract user-friendly information from the URL (with fallback for undefined)
    const { displayName } = extractUserFriendlyUrlInfo(url || 'Unknown source');
    title = t('import.failed.title');

    // Use errorCode for translated message, fall back to raw error
    let errorMessage = error || t('import.failed.description');
    if (errorCode) {
      const translationKey = `import.failed.errors.${errorCode}`;
      const translated = t(translationKey);
      if (translated !== translationKey) {
        errorMessage = translated;
      }
    }
    message = `${t('import.failed.notificationPrefix', { name: displayName, defaultValue: `Failed to import ${displayName}` })}: ${errorMessage}`;
    link = undefined;
    // Clean up cache on failure
    if (fileId) {
      importUrlCache.delete(fileId);
    }
  } else if (status === 'completed') {
    // Extract user-friendly information from the URL (with fallback for undefined)
    const { displayName } = extractUserFriendlyUrlInfo(url || 'Unknown source');
    title = t('import.complete.title', { defaultValue: 'Import Complete' });
    message = t('import.complete.notification', {
      name: displayName,
      defaultValue: `Successfully imported ${displayName}`,
    });
    link = getFileUrl({ teamId, parentId, fileId, withBasePath: true });
    // Clean up cache on completion
    if (fileId) {
      importUrlCache.delete(fileId);
    }
  } else {
    // Comment out processing notification - users don't need to see this
    // title = 'Processing Import';
    // message = `Processing ${displayName} from ${source}...`;
    // link = undefined;

    // Don't show processing notifications, just return early
    dispatch(triggerFileListRefresh());
    return;
  }

  const notificationData = {
    id: fileId,
    type: 'import' as const,
    title,
    message,
    link,
    read: false,
    timestamp: Date.now(),
  };

  // Use updateNotification to replace any existing notification for this file
  // This prevents multiple notifications for the same import
  dispatch(updateNotification(notificationData));

  // Trigger a refresh of the file list
  dispatch(triggerFileListRefresh());
};

const handleFileUpdate = async (
  payload: UpdateMessageMap['fileUpdate']['payload'],
) => {
  const { dispatch, getState } = store;
  const state = getState() as RootState;
  const { teamId, parentId, fileId, name, error } = payload;
  const { _id } = state?.selectedVideo?.file;
  const trimStatus = state?.selectedVideo?.actions?.trim?.status;

  // Check if we're waiting for a trim operation on this file
  if (fileId === _id && trimStatus === 'pending') {
    if (error) {
      dispatch(setTrimStatus('failed'));
    } else {
      dispatch(setTrimStatus('succeeded'));
    }
  }

  // If this is the currently selected file, fetch the updated file without changing loading state
  if (fileId === _id) {
    dispatch(fetchFileUpdate(fileId));
  } else {
    // Update the file in the library
    dispatch(
      updateFile({
        _id: fileId,
        name,
      }),
    );

    // Check if a notification for this file already exists
    const existingNotification = state.dashboard.notifications.find(
      (notification) => notification.id === fileId,
    );

    // Create an engaging, user-friendly notification
    const notificationTitle = name ? `${name}` : 'Your recording is ready';
    const notificationMessage = name
      ? 'Transcription and AI complete'
      : 'Processing complete';

    // Use updateNotification to either create or update the notification for this file
    // This prevents duplicate notifications for the same file across different processing stages
    dispatch(
      updateNotification({
        id: fileId,
        type: 'transcription',
        title: notificationTitle,
        message: notificationMessage,
        link: getFileUrl({ teamId, parentId, fileId, withBasePath: true }),
        read: existingNotification?.read ?? false,
        timestamp: existingNotification?.timestamp ?? Date.now(),
      }),
    );
  }
};

const handleJobUpdate = (payload: UpdateMessageMap['jobUpdate']['payload']) => {
  const { dispatch, getState } = store;
  const state = getState() as RootState;
  const { _id } = state?.selectedVideo?.file;

  if (_id === payload.recordingId && payload.job === 'transcribe') {
    dispatch(updateJobStatus({ ...payload, job: 'transcribe' }));
  }
};

const handleInvitationUpdate = (
  payload: UpdateMessageMap['invitation']['payload'],
) => {
  const { dispatch } = store;
  const { teamId, teamName, memberEmail, memberName } = payload;

  // Create notification for invitation accepted
  const notificationData = {
    id: `invitation-${teamId}-${Date.now()}`,
    type: 'invitation' as const,
    title: 'Team Invitation Accepted',
    message: `${memberName || memberEmail} has joined ${teamName}`,
    // link: `/#/team/${teamId}/members`,
    read: false,
    timestamp: Date.now(),
  };

  dispatch(updateNotification(notificationData));
};

const handleTeamMemberRemoved = (
  payload: UpdateMessageMap['teamMemberRemoved']['payload'],
) => {
  const { dispatch, getState } = store;
  const state = getState() as RootState;
  const currentUser = state.settings.user;

  // If current user was removed, force refresh to update UI
  if (currentUser?._id === payload.removedUserId) {
    // Show notification
    const notificationData = {
      id: `member-removed-${payload.teamId}-${Date.now()}`,
      type: 'team' as const,
      title: 'Removed from Team',
      message: `You have been removed from ${payload.teamName}`,
      read: false,
      timestamp: Date.now(),
    };

    dispatch(updateNotification(notificationData));

    // Refresh app state then redirect to new team
    // Cast needed because store uses legacy createStore without thunk type inference
    (
      dispatch(getInitialState({})) as unknown as Promise<{
        meta: { requestStatus: string };
      }>
    ).then((result) => {
      if (result.meta.requestStatus === 'fulfilled') {
        const newState = getState() as RootState;
        const newTeamId = newState.team.selectedTeam?.teamId;
        if (newTeamId) {
          window.location.hash = `#/library/${newTeamId}/recents`;
        }
      }
    });
  }
};

const handleTeamMemberJoined = (
  payload: UpdateMessageMap['teamMemberJoined']['payload'],
) => {
  const { dispatch } = store;
  const { teamId, teamName, member } = payload;

  // Update Redux state with new member
  dispatch(addTeamMember({ teamId, member }));

  // Show notification
  const notificationData = {
    id: `member-joined-${teamId}-${member._id}-${Date.now()}`,
    type: 'team' as const,
    title: 'New Team Member',
    message: `${member.name || member.email} has joined ${teamName}`,
    read: false,
    timestamp: Date.now(),
  };
  dispatch(updateNotification(notificationData));
};

const handleResourceShared = (
  payload: UpdateMessageMap['resourceShared']['payload'],
) => {
  const { dispatch } = store;
  const { resourceName, resourceType, sharedByUserName, teamId, resourceId } =
    payload;

  const notificationData = {
    id: `resource-shared-${resourceId}-${Date.now()}`,
    type: 'share' as const,
    title: 'Content Shared With You',
    message: `${sharedByUserName} shared a ${resourceType.toLowerCase()} "${resourceName}" with you`,
    link: getFileUrl({
      teamId,
      parentId: 'recents',
      fileId: resourceId,
      withBasePath: true,
    }),
    read: false,
    timestamp: Date.now(),
  };

  dispatch(updateNotification(notificationData));

  // Refresh library to show newly shared content
  dispatch(triggerFileListRefresh());
};

const handleCloneShareProcessed = (
  payload: UpdateMessageMap['cloneShareProcessed']['payload'],
) => {
  const { dispatch } = store;
  const { resourceName, resourceType, clonedByUserName, clonedByUserEmail } =
    payload;

  const notificationData = {
    id: `clone-share-${payload.resourceId}-${Date.now()}`,
    type: 'share' as const,
    title: 'Content Viewed',
    message: `${clonedByUserName || clonedByUserEmail} viewed your shared ${resourceType.toLowerCase()} "${resourceName}"`,
    read: false,
    timestamp: Date.now(),
  };

  dispatch(updateNotification(notificationData));
};

const updateHandler = (update: UpdateMessageMap[keyof UpdateMessageMap]) => {
  console.log('socket update,', update);
  try {
    const { type, payload } = update;
    const { dispatch } = store;
    logger.info(`socket: Received ${type} update`, payload);
    switch (type) {
      case 'usage':
        dispatch(setUsage(payload));
        break;
      case 'file':
        handleFileUpdate(payload).catch((error) => {
          logger.error('socket: Error handling file update', error);
        });
        break;
      case 'job':
        handleJobUpdate(payload);
        break;
      case 'rag':
        handleRagUpdate(payload);
        logger.info('socket: Received RAG update');
        break;
      case 'import':
        handleImportUpdate(payload);
        break;
      case 'team:invitation:accepted':
        handleInvitationUpdate(payload);
        break;
      case 'team:member:removed':
        handleTeamMemberRemoved(payload);
        break;
      case 'team:member:joined':
        handleTeamMemberJoined(payload);
        break;
      case 'resource:shared':
        handleResourceShared(payload);
        break;
      case 'clone:share:processed':
        handleCloneShareProcessed(payload);
        break;
      default:
        logger.warn(`socket: Unknown update type ${type}`);
        break;
    }
  } catch (error) {
    logger.error('socket: Error handling update', error);
  }
};

export default updateHandler;
